/**
 * Monthly add-ons of a paid Music24 plan (spec 2026-10-19 monthly add-ons, owner decisions of
 * 2026-10-19). A web subscriber (Stripe) adds extra scheduled posts and extra video credits to the
 * plan, each month. The one-time credit packs (`creditPacks.ts`) stay as they are.
 *
 * The price is per unit with volume tiers (graduated): each unit costs the price of the tier that it
 * falls in. Stripe bills the same tiers (`billing_scheme: tiered`, `tiers_mode: graduated`), USD and
 * EUR at the same number. A yearly plan bills the add-ons for a year at ten months
 * (`MUSIC24_MONTHS_PER_YEARLY_PRICE`): each tier price times ten.
 *
 * The web app, the server and the Stripe script read the tiers here, so the slider total and the
 * Stripe invoice agree. The only import is `music24Plans.ts` (no heavy dependency): every project
 * can use the file.
 *
 * The extra artists of a paid plan are the third row of the section (owner request 2026-10-19
 * "same slider for extra artists"). They are not a `MonthlyAddOnKind`: they keep the extra-artist
 * item and its flat price per artist (`music24ExtraArtistMonthlyCents`: the plan price minus 20 %,
 * owner decision 2026-10-23), and the resolver adds them through `extraArtists`, not through
 * `withMonthlyAddOns`.
 */
import {
  type Music24Capabilities,
  type Music24PlanTier,
  music24ExtraArtistMonthlyCents,
  music24PlanCapabilities,
} from './music24Plans';

/** The two add-ons. `posts` adds to `scheduledPostsPerMonth`, `videoCredits` to `videoCreditsPerMonth`. */
export type MonthlyAddOnKind = 'posts' | 'videoCredits';

export const MONTHLY_ADD_ON_KINDS: readonly MonthlyAddOnKind[] = ['posts', 'videoCredits'];

/** One volume tier. `upTo` is the last unit of the tier (inclusive); null is no end. */
export interface MonthlyAddOnTier {
  upTo: number | null;
  /** The monthly price of one unit in the tier, in US cents (EUR the same number). */
  unitCents: number;
}

/**
 * The tiers of each add-on (owner decision 2 of 2026-10-19).
 * - Posts: 1-100 at $0.10, 101-500 at $0.07, 501 and up at $0.05 a post a month.
 * - Video credits: 1-50 at $1.00, 51-200 at $0.80, 201 and up at $0.60 a credit a month.
 */
export const MONTHLY_ADD_ON_TIERS: Readonly<Record<MonthlyAddOnKind, readonly MonthlyAddOnTier[]>> = {
  posts: [
    { upTo: 100, unitCents: 10 },
    { upTo: 500, unitCents: 7 },
    { upTo: null, unitCents: 5 },
  ],
  videoCredits: [
    { upTo: 50, unitCents: 100 },
    { upTo: 200, unitCents: 80 },
    { upTo: null, unitCents: 60 },
  ],
};

/** The slider of an add-on: from 0 to `max` in steps of `step` (owner decision 3). */
export interface MonthlyAddOnSlider {
  step: number;
  max: number;
}

export const MONTHLY_ADD_ON_SLIDERS: Readonly<Record<MonthlyAddOnKind, MonthlyAddOnSlider>> = {
  posts: { step: 10, max: 2000 },
  videoCredits: { step: 10, max: 1000 },
};

/** The units of each add-on. 0 is none. */
export type MonthlyAddOnQuantities = Record<MonthlyAddOnKind, number>;

/** The billing interval of the plan subscription. */
export type MonthlyAddOnInterval = 'monthly' | 'yearly';

/** A yearly plan bills the add-ons for this many months (the same rule as the plans). */
export const MONTHLY_ADD_ON_MONTHS_PER_YEAR = 10;

/** True for a value that the slider can give: a whole number, a step, from 0 to the max. */
export function isValidMonthlyAddOnQuantity(kind: MonthlyAddOnKind, quantity: number): boolean {
  const slider = MONTHLY_ADD_ON_SLIDERS[kind];
  return Number.isInteger(quantity) && quantity >= 0 && quantity <= slider.max && quantity % slider.step === 0;
}

/**
 * The monthly price of `quantity` units of one add-on in US cents, graduated: each unit costs the
 * price of its tier. 0 for 0 units. Pure.
 */
export function monthlyAddOnCents(kind: MonthlyAddOnKind, quantity: number): number {
  const units = Math.max(0, Math.floor(quantity));
  let cents = 0;
  let previousUpTo = 0;
  for (const tier of MONTHLY_ADD_ON_TIERS[kind]) {
    if (units <= previousUpTo) {
      break;
    }
    const tierEnd = tier.upTo === null ? units : Math.min(units, tier.upTo);
    cents += (tierEnd - previousUpTo) * tier.unitCents;
    if (tier.upTo === null) {
      break;
    }
    previousUpTo = tier.upTo;
  }
  return cents;
}

/** The extra artists of a paid plan in the add-on section: the plan and the number. */
export interface ExtraArtistAddOn {
  plan: Music24PlanTier;
  quantity: number;
}

/** The slider of the extra artists moves in steps of one artist (orchestrator decision 3). */
export const EXTRA_ARTIST_ADD_ON_STEP = 1;

/**
 * True when the plan sells extra artists: its `extraArtistLimit` in the plan table is not 0 (owner
 * decision 2026-10-23: Plus, Pro, Max and Ultra). Free, the trial and Plug show no artists slider.
 */
export function planHasExtraArtistAddOn(plan: Music24PlanTier): boolean {
  return music24PlanCapabilities[plan].extraArtistLimit !== 0;
}

/**
 * The monthly price of `quantity` extra artists in US cents: a flat integer price per artist, no
 * tiers (`music24ExtraArtistMonthlyCents`: Plus 1520, Pro 3120, Max 5520, Ultra 19920). 0 for a
 * plan with no extra artists. Pure.
 */
export function extraArtistAddOnMonthlyCents(plan: Music24PlanTier, quantity: number): number {
  if (plan === 'free' || plan === 'trial' || !planHasExtraArtistAddOn(plan)) {
    return 0;
  }
  return Math.max(0, Math.floor(quantity)) * music24ExtraArtistMonthlyCents(plan);
}

/** The extra artists that the artists of the workspace use now: the artists above the included ones. Pure. */
export function extraArtistsInUse(artistCount: number, includedArtists: number): number {
  return Math.max(0, Math.floor(artistCount) - includedArtists);
}

/**
 * The lowest value of the artists slider (orchestrator decision 4): the extra artists in use. A
 * workspace that holds more artists than it pays for (read-only artists after a drop) can keep its
 * current value, so the floor is never above `current`. Pure.
 */
export function extraArtistAddOnMinimum(current: number, inUse: number): number {
  return Math.max(0, Math.min(current, inUse));
}

/**
 * The price of the add-ons for one billing period of `interval`, in US cents. With `extraArtists`
 * the price includes the extra artists of a team plan. Pure.
 */
export function monthlyAddOnsPeriodCents(
  addOns: MonthlyAddOnQuantities,
  interval: MonthlyAddOnInterval,
  extraArtists?: ExtraArtistAddOn
): number {
  const monthly =
    monthlyAddOnCents('posts', addOns.posts) +
    monthlyAddOnCents('videoCredits', addOns.videoCredits) +
    (extraArtists === undefined ? 0 : extraArtistAddOnMonthlyCents(extraArtists.plan, extraArtists.quantity));
  return interval === 'yearly' ? monthly * MONTHLY_ADD_ON_MONTHS_PER_YEAR : monthly;
}

/**
 * The tier of the next unit, for the hint under a slider ("Next 400 posts: $0.07 each"). Null
 * when the quantity is at the slider end. Pure.
 */
export function nextMonthlyAddOnTier(
  kind: MonthlyAddOnKind,
  quantity: number
): { unitCents: number; unitsLeftInTier: number | null } | null {
  if (quantity >= MONTHLY_ADD_ON_SLIDERS[kind].max) {
    return null;
  }
  const tier = MONTHLY_ADD_ON_TIERS[kind].find((entry) => entry.upTo === null || quantity < entry.upTo);
  if (tier === undefined) {
    return null;
  }
  return { unitCents: tier.unitCents, unitsLeftInTier: tier.upTo === null ? null : tier.upTo - quantity };
}

/** Only a paid plan gets add-ons (owner decision 4): Free and the trial cannot buy them. */
export function planCanHaveMonthlyAddOns(plan: Music24PlanTier): boolean {
  return plan !== 'free' && plan !== 'trial';
}

/**
 * Adds the add-ons to the limits of a plan (owner decision 5). Extra posts add to
 * `scheduledPostsPerMonth`, extra credits to `videoCreditsPerMonth` (on a team plan that is the
 * pool, not a number per artist). A limit of null (no limit) stays null. Free and the trial get
 * nothing. Pure.
 */
export function withMonthlyAddOns(
  capabilities: Music24Capabilities,
  addOns: Partial<MonthlyAddOnQuantities> | undefined
): Music24Capabilities {
  const posts = Math.max(0, Math.floor(addOns?.posts ?? 0));
  const videoCredits = Math.max(0, Math.floor(addOns?.videoCredits ?? 0));
  if ((posts === 0 && videoCredits === 0) || !planCanHaveMonthlyAddOns(capabilities.plan)) {
    return capabilities;
  }
  return {
    ...capabilities,
    scheduledPostsPerMonth:
      capabilities.scheduledPostsPerMonth === null ? null : capabilities.scheduledPostsPerMonth + posts,
    videoCreditsPerMonth:
      capabilities.videoCreditsPerMonth === null ? null : capabilities.videoCreditsPerMonth + videoCredits,
  };
}
